import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { ExpandOutlined, LoadingOutlined, PlayCircleOutlined } from '@ant-design/icons';

import { formatDuration, useVideoMeta, videoPosterUrl, videoStreamUrl } from '../editor/video-node';

const SPEEDS = [0.75, 1, 1.25, 1.5, 2] as const;
const POS_KEY = (id: string): string => `wiki-video-pos:${id}`;

/**
 * Свой плеер читалки (фаза 0.2): нативный <video> с нашего S3 + обвязка —
 * постер, скорость, запоминание позиции (localStorage), «обрабатывается»
 * пока бэк транскодирует. Монтируется в <figure data-video> из bodyHtml
 * (гидрация в ArticlePage, как fk-table).
 */
export function FkVideoPlayer({
  id,
  caption,
  startAt,
  onProgress,
  seek,
  onTime,
  fullscreenButton,
}: {
  id: string;
  caption: string;
  /** Позиция с сервера (уроки, фаза 2.1) — приоритетнее localStorage. */
  startAt?: number | null;
  /** Уроки: позиция/длительность раз в ~10 с, на паузе и в конце (досмотр = зачёт). */
  onProgress?: (positionSec: number, durationSec: number) => void;
  /** HUB 2.0: переход к главе — { t: секунда, n: счётчик нажатий (повтор той же главы) }. */
  seek?: { t: number; n: number } | null;
  /** HUB 2.0: текущая секунда (подсветка главы), не чаще раза в секунду. */
  onTime?: (sec: number) => void;
  /** HUB 2.0: кнопка «На весь экран» (уроки; на iPhone — нативный полноэкранный плеер). */
  fullscreenButton?: boolean;
}): JSX.Element {
  const meta = useVideoMeta(id);
  const ref = useRef<HTMLVideoElement | null>(null);
  const timeRef = useRef(onTime);
  timeRef.current = onTime;
  const lastSec = useRef(-1);
  const [speed, setSpeed] = useState<number>(1);
  const ready = meta?.status === 'ready';
  const progressRef = useRef(onProgress);
  progressRef.current = onProgress;

  // Позиция просмотра: восстанавливаем при загрузке метаданных, пишем раз в 5 с.
  useEffect(() => {
    const v = ref.current;
    if (!v || !ready) return;
    let last = 0;
    let lastServer = 0;
    const report = (force: boolean): void => {
      if (!progressRef.current || !Number.isFinite(v.duration) || v.duration <= 0) return;
      const now = Date.now();
      if (!force && now - lastServer < 10_000) return;
      lastServer = now;
      progressRef.current(Math.floor(v.currentTime), Math.floor(v.duration));
    };
    const onMeta = (): void => {
      try {
        const saved = startAt != null && startAt > 0 ? startAt : Number(localStorage.getItem(POS_KEY(id)) || 0);
        if (saved > 5 && saved < v.duration - 5) v.currentTime = saved;
      } catch { /* localStorage недоступен — просто с начала */ }
    };
    const onTime = (): void => {
      const now = Date.now();
      const sec = Math.floor(v.currentTime);
      if (timeRef.current && sec !== lastSec.current) {
        lastSec.current = sec;
        timeRef.current(sec);
      }
      report(false);
      if (now - last < 5000) return;
      last = now;
      try { localStorage.setItem(POS_KEY(id), String(Math.floor(v.currentTime))); } catch { /* ignore */ }
    };
    const onPause = (): void => report(true);
    const onEnded = (): void => {
      try { localStorage.removeItem(POS_KEY(id)); } catch { /* ignore */ }
      if (progressRef.current && Number.isFinite(v.duration) && v.duration > 0) {
        progressRef.current(Math.floor(v.duration), Math.floor(v.duration));
      }
    };
    v.addEventListener('loadedmetadata', onMeta);
    v.addEventListener('timeupdate', onTime);
    v.addEventListener('pause', onPause);
    v.addEventListener('ended', onEnded);
    return () => {
      v.removeEventListener('loadedmetadata', onMeta);
      v.removeEventListener('timeupdate', onTime);
      v.removeEventListener('pause', onPause);
      v.removeEventListener('ended', onEnded);
    };
  }, [id, ready, startAt]);

  useEffect(() => {
    if (ref.current) ref.current.playbackRate = speed;
  }, [speed, ready]);

  // Глава: перемотать и запустить (повторное нажатие той же главы — тоже).
  useEffect(() => {
    const v = ref.current;
    if (!v || !ready || !seek) return;
    try {
      v.currentTime = Math.max(0, seek.t);
      void v.play().catch(() => undefined);
    } catch {
      /* метаданные ещё не загружены — пропускаем */
    }
  }, [seek, ready]);

  const goFullscreen = (): void => {
    const v = ref.current as (HTMLVideoElement & { webkitEnterFullscreen?: () => void }) | null;
    if (!v) return;
    if (v.requestFullscreen) {
      v.requestFullscreen().catch(() => v.webkitEnterFullscreen?.());
      return;
    }
    // iPhone Safari: полноэкранный режим только у самого <video>.
    v.webkitEnterFullscreen?.();
  };

  if (!ready) {
    return (
      <div className="fk-video-processing">
        {meta?.status === 'error' ? (
          <>
            <PlayCircleOutlined style={{ fontSize: 28 }} />
            <div>Видео недоступно: {meta.error ?? 'ошибка обработки'}</div>
          </>
        ) : (
          <>
            <LoadingOutlined spin style={{ fontSize: 28 }} />
            <div>Видео обрабатывается — попробуйте через минуту</div>
          </>
        )}
      </div>
    );
  }

  return (
    <>
      <video
        ref={ref}
        controls
        preload="metadata"
        playsInline
        poster={meta?.hasPoster ? videoPosterUrl(id) : undefined}
        src={videoStreamUrl(id)}
      />
      <div className="fk-video-bar">
        {caption && <span className="fk-video-caption">{caption}</span>}
        {meta?.durationSec != null && <span className="fk-video-dur">{formatDuration(meta.durationSec)}</span>}
        {fullscreenButton && (
          <button type="button" className="fk-video-fs" onClick={goFullscreen} title="На весь экран" style={{ font: 'inherit', fontSize: 12, fontWeight: 600, padding: '3px 10px', minHeight: 32, borderRadius: 6, border: '1px solid var(--fk-border)', background: 'var(--fk-surface)', color: 'var(--fk-text-2)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <ExpandOutlined /> На весь экран
          </button>
        )}
        <span className="fk-video-speeds">
          {SPEEDS.map((s) => (
            <button key={s} type="button" data-on={s === speed} onClick={() => setSpeed(s)} title={`Скорость ${s}×`}>
              {s}×
            </button>
          ))}
        </span>
      </div>
    </>
  );
}
